import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Check, Copy } from "lucide-react";
import { cn } from "../../lib/cn";
import { BLOCK_SURFACE } from "./blockSurface";

/**
 * KeyValue - a definition / spec list of label -> value pairs (entity
 * attributes, a config dump, response metadata). Renderer for the K2
 * `key_value` block (`docs/contracts/block-registry.md`): the platform's chat
 * `BlockRenderer` passes a `key_value` block's `props` straight through, so the
 * prop names match the block payload exactly.
 *
 * Block props (`{ title?, pairs, columns? }`):
 *   - `title?` - optional heading above the list.
 *   - `pairs` - the entries, each `{ label, value, href?, copyable? }`:
 *       - `label` - the term (rendered in a `<dt>`).
 *       - `value` - the value (rendered in a `<dd>`). string | number.
 *       - `href?` - when set, the value renders as a safe external link.
 *       - `copyable?` - when set, a copy button sits next to the value.
 *   - `columns?` - `1` (default, stacked) or `2` (a two-column grid on wider
 *     viewports; collapses to one column on narrow ones).
 *
 * Client-only props (never part of the streamed block): `className`.
 *
 * Rendered as a real `<dl>` so screen readers announce term / definition
 * pairs. Links go through the same safe-URL guard the citation widgets use
 * (http/https/mailto only). The copy button is feature-checked
 * (`navigator.clipboard`), runs only in `onClick`, and silently no-ops when
 * unavailable - SSR-safe. Token-themed (`border-border`, `text-foreground`,
 * `text-muted-foreground`, ...) with no hardcoded colors.
 */

/** Source: ubiquitous (CopilotKit declarative rows, OG "details" cards). */
export interface KeyValuePair {
  /** The term. */
  label: string;
  /** The value. */
  value: string | number;
  /** When set, the value renders as a safe external link. */
  href?: string;
  /** When set, a copy-to-clipboard button sits next to the value. */
  copyable?: boolean;
}

const keyValueVariants = cva(
  cn("not-prose flex flex-col gap-3", BLOCK_SURFACE),
  {
    variants: {
      columns: {
        1: "",
        2: "",
      },
    },
    defaultVariants: { columns: 1 },
  },
);

/** Allow only safe schemes for the optional `href` (mirrors the citation guard). */
function safeHref(href: string | undefined): string | undefined {
  if (!href) return undefined;
  try {
    const u = new URL(href, "https://placeholder.invalid");
    if (u.protocol === "http:" || u.protocol === "https:" || u.protocol === "mailto:") {
      return href;
    }
  } catch {
    // Unparseable - treat as unsafe.
  }
  return undefined;
}

export interface KeyValueProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof keyValueVariants> {
  /** Optional heading above the list. */
  title?: string;
  /** The entries. Required. */
  pairs: KeyValuePair[];
}

export const KeyValue = React.forwardRef<HTMLDivElement, KeyValueProps>(function KeyValue(
  { className, title, pairs, columns, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(keyValueVariants({ columns }), className)}
      data-slot="key-value"
      {...rest}
    >
      {title ? <p className="text-sm font-semibold leading-none text-foreground">{title}</p> : null}
      <dl
        className={cn(
          "grid gap-x-6 gap-y-2.5 text-sm",
          columns === 2 ? "sm:grid-cols-2" : "grid-cols-1",
        )}
      >
        {pairs.map((pair, i) => (
          <div
            key={`${pair.label}-${i}`}
            className="flex flex-col gap-0.5 border-b border-border/50 pb-2 last:border-b-0 sm:last:border-b-0"
          >
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {pair.label}
            </dt>
            <dd className="flex min-w-0 items-center gap-1.5 font-medium text-foreground">
              <KeyValueValue pair={pair} />
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
});

KeyValue.displayName = "KeyValue";

/* ============================================================================
 * Internal - one value cell (link + copy affordances)
 * ========================================================================= */

function KeyValueValue({ pair }: { pair: KeyValuePair }) {
  const text = String(pair.value);
  const href = safeHref(pair.href);

  const [copied, setCopied] = React.useState(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const handleCopy = React.useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(text).catch(() => {
      // Permission denied / focus loss - silent no-op.
    });
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1500);
  }, [text]);

  return (
    <>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="truncate text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:rounded"
        >
          {text}
        </a>
      ) : (
        <span className="truncate tabular-nums">{text}</span>
      )}
      {pair.copyable ? (
        <button
          type="button"
          onClick={handleCopy}
          aria-label={copied ? `Copied ${pair.label}` : `Copy ${pair.label}`}
          className="shrink-0 rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {copied ? (
            <Check className="size-3.5" aria-hidden />
          ) : (
            <Copy className="size-3.5" aria-hidden />
          )}
        </button>
      ) : null}
    </>
  );
}

export { keyValueVariants };
export type KeyValueVariantProps = VariantProps<typeof keyValueVariants>;
